import React, { useEffect, useState } from 'react';
import { Box, Button, lighten, Typography, useTheme } from '@mui/material';
import {
    BoxSkeleton,
    ConfirmationModal,
    SkeletonLoader,
    useModal,
} from '@europrocurement/flexy-components';
import {
    ArticleCategory,
    CATEGORY_BODACC,
    CATEGORY_GREFFE,
    CATEGORY_LEGAL_ANNOUCEMENT,
    CATEGORY_TAXES,
    useDossierArticleContext,
    useDossierValidatorsCtx,
    useSelectedDossierContext,
} from '@europrocurement/l2d-modules';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { enqueueSnackbar } from 'notistack';
import DossierFormaliteProcesses from '../DossierFormaliteProcesses/DossierFormaliteProcesses';
import DossierLegalAnnoucements from '../DossierLegalAnnoucements/DossierLegalAnnoucements';
import DossierBodaccProcesses from '../DossierBodaccProcesses/DossierBodaccProcesses';
import DossierImpotProcesses from '../DossierImpotProcesses/DossierImpotProcesses';
import {
    AddArticleParamsType,
    DossierArticle,
    useDossierArticle,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
} from '../../hooks';
import DisplayDossierProcessesBlocForm from '../DisplayDossierProcessesBlocForm/DisplayDossierProcessesBlocForm';
import DossierArticlesProcesses from '../DossierArticlesProcesses/DossierArticlesProcesses';
import DossierProcessesFooter from '../DossierProcessesFooter/DossierProcessesFooter';
import AddProcessForm from '../AddProcessForm/AddProcessForm';
import { AddTaxFormData } from '../AddTaxForm/AddTaxForm';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';

const DossierProcesses: React.FunctionComponent = function () {
    const { dossier, refreshDossier } = useSelectedDossierContext();
    const { addArticle, deleteArticle, getArticlesByDossier } = useDossierArticle();
    const {
        createValidatorProcess,
        canCreateValidatorProcess,
        findProcessByArticle,
        getArticleProcessesStatus,
        getArticleProcessesStatusLabel,
        getArticleProcessesStatusColor,
    } = useDossierProcesses();
    const { actionUpdateValidatorStatus } = useDossierProcessesActionsAndRender();
    const { palette } = useTheme();
    const { modalActions } = useModal();
    const { articleCategories } = useDossierArticleContext();
    const { getDossierValidatorById } = useDossierValidatorsCtx();

    const [articlesLoaded, setArticlesLoaded] = useState<boolean>(false);
    const [articles, setArticles] = useState<Array<DossierArticle>>([]);
    const [blockDisplayed, setBlockDisplayed] = useState<Array<number>>([]);

    const refreshArticles = async (dossierId: number) => {
        setArticlesLoaded(false);

        try {
            const response = await getArticlesByDossier(dossierId);
            setArticles(response);
        } catch (e) {
            console.error(e);
        } finally {
            setBlockDisplayed([]);
            setArticlesLoaded(true);
        }
    };

    useEffect(() => {
        if (dossier.id) {
            refreshArticles(dossier.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier.id]);

    const findArticlesByCategory = (categoryId: number): Array<DossierArticle> => {
        if (!articlesLoaded) {
            return [];
        }

        return articles.filter((article) => article.categoryId === categoryId);
    };

    const findBodaccArticles = (): Array<DossierArticle> => {
        const bodaccArticles = findArticlesByCategory(CATEGORY_BODACC);

        return bodaccArticles.filter((res) => res.article.rubArticle === 'FRN');
    };

    const onSubmitProcess = async (data: AddTaxFormData) => {
        try {
            const articleId = await addArticle({
                prestationId: data.prestationId,
                label: data.title,
                factArticleId: data.articleId,
            } as AddArticleParamsType);

            await createValidatorProcess(dossier.id as number, [articleId]);
            refreshDossier();
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Une erreur est survenue lors de l'ajout de votre démarche", {
                variant: 'error',
            });
        } finally {
            modalActions.close();
        }
    };

    const onRemoveProcess = async (articleId: string) => {
        try {
            await deleteArticle(articleId);
            refreshDossier();
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lros de la suppression de votre démarche', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
        }
    };

    const getItemActions = (
        article: DossierArticle,
        validatorTypeId: number,
    ): Array<DossierArticleProcessMenuItemProps> => {
        const actions = [];
        const process = findProcessByArticle(dossier, [validatorTypeId], article.id);
        const statuses = process
            ? getDossierValidatorById(process.validatorId)?.statuses || []
            : [];

        if (process) {
            actions.push(
                actionUpdateValidatorStatus(() => {
                    modalActions.call(
                        <UpdateValidatorForm
                            processing={false}
                            process={process}
                            onItemSelected={() => {
                                modalActions.close();
                                refreshDossier();
                            }}
                            statuses={statuses}
                        />,
                    );
                }),
            );
        }

        actions.push({
            label: 'Supprimer la démarche',
            icon: 'trash',
            onClick: (currentArticle: DossierArticle) => {
                modalActions.call(
                    <ConfirmationModal
                        color={palette.error}
                        messages={{
                            question: 'Confirmer cette action ?',
                            detail: {
                                message: 'Êtes vous sur de vouloir supprimer la démarche ?',
                                severity: 'error',
                            },
                            validation: 'Supprimer',
                        }}
                        actionOnValidation={() => onRemoveProcess(currentArticle.id.toString())}
                        actionOnCancellation={() => modalActions.close()}
                    />,
                );
            },
        });

        return actions as Array<DossierArticleProcessMenuItemProps>;
    };

    const getMinSortOrderInCategory = (category: ArticleCategory): number => {
        const articlesFromCategory =
            category.id === CATEGORY_BODACC
                ? findBodaccArticles()
                : findArticlesByCategory(category.id);

        return articlesFromCategory.reduce(
            (minRang, current) => (current.rang < minRang ? current.rang : minRang),
            999,
        );
    };

    const blocks = articleCategories
        .sort((categoryA, categoryB) => {
            const minArticlesByCategoryA = getMinSortOrderInCategory(categoryA);
            const minArticlesByCategoryB = getMinSortOrderInCategory(categoryB);

            return minArticlesByCategoryA > minArticlesByCategoryB ? 1 : -1;
        })
        .filter((articleCategory) => articleCategory.validatorTypeId)
        .map((articleCategory) => {
            let checked = blockDisplayed.includes(articleCategory.id);

            if (articleCategory.id === CATEGORY_GREFFE) {
                checked = checked || dossier.formalites.length > 0;
            } else if (articleCategory.id === CATEGORY_BODACC) {
                checked = checked || findBodaccArticles().length > 0;
            } else {
                checked = checked || findArticlesByCategory(articleCategory.id).length > 0;
            }

            return {
                id: articleCategory.id,
                label: articleCategory.label,
                validatorTypeId: articleCategory.validatorTypeId,
                checked,
            };
        });

    const isCategoryVisible = (idCategory: number) => {
        const category = blocks.find((c) => c.id === idCategory);

        return category?.checked || false;
    };

    const showToggleProcessBlockModal = () => {
        modalActions.call(
            <DisplayDossierProcessesBlocForm
                items={blocks}
                onToggleItem={(idArticleCategory: number) => {
                    const proxy = [...blockDisplayed];

                    if (proxy.includes(idArticleCategory)) {
                        proxy.splice(proxy.indexOf(idArticleCategory), 1);
                    } else {
                        proxy.push(idArticleCategory);
                    }

                    setBlockDisplayed(proxy);
                    modalActions.close();
                }}
            />,
        );
    };

    const showAddProcessModal = (categoryId: number) => {
        modalActions.call(
            <AddProcessForm
                categoryId={categoryId}
                dossier={dossier}
                onSubmit={onSubmitProcess}
                onCancel={() => modalActions.close()}
            />,
        );
    };

    return (
        <SkeletonLoader
            isLoading={!articlesLoaded}
            containerSx={{
                marginTop: '10px',
                marginBottom: '10px',
            }}
            skeleton={
                <BoxSkeleton
                    sx={{
                        height: '250px',
                    }}
                />
            }
        >
            <Box
                sx={{
                    padding: '0 0 16px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px 0',
                }}
            >
                <Box
                    sx={{
                        padding: '10px 0 0 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        variant="l2dsubtitles"
                        data-testid="title-dossier-process"
                    >
                        Démarches
                    </Typography>
                    <Button
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0 8px',
                            color: lighten(palette.secondary.main, 0.3),
                        }}
                        variant="text"
                        onClick={showToggleProcessBlockModal}
                    >
                        <FaOptionIcon
                            icon={['fas', 'plus']}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                lineHeight: 0,
                            }}
                        />
                        <Typography variant="l2dcaption">Ajouter une démarche</Typography>
                    </Button>
                </Box>

                {blocks
                    .filter((category) => isCategoryVisible(category.id))
                    .map((category) => {
                        if (category.id === CATEGORY_GREFFE) {
                            return <DossierFormaliteProcesses key={category.label} />;
                        }

                        if (category.id === CATEGORY_LEGAL_ANNOUCEMENT) {
                            return (
                                <DossierLegalAnnoucements
                                    key={category.label}
                                    articles={findArticlesByCategory(category.id)}
                                />
                            );
                        }

                        if (category.id === CATEGORY_BODACC) {
                            return (
                                <DossierBodaccProcesses
                                    key={category.label}
                                    articles={findBodaccArticles()}
                                />
                            );
                        }

                        if (category.id === CATEGORY_TAXES) {
                            return (
                                <DossierImpotProcesses
                                    key={category.label}
                                    articles={findArticlesByCategory(category.id)}
                                />
                            );
                        }

                        const footer = canCreateValidatorProcess(
                            dossier,
                            category.validatorTypeId,
                        ) ? (
                            <DossierProcessesFooter
                                key={category.label}
                                handleAddItem={() => showAddProcessModal(category.id)}
                                addItemTextBtn="Ajouter un enregistrement"
                                canAddItem
                            />
                        ) : null;

                        const processArticles = findArticlesByCategory(category.id);
                        const status = getArticleProcessesStatus(
                            dossier,
                            category.validatorTypeId,
                            processArticles.map((a) => a.id),
                        );

                        return (
                            <DossierArticlesProcesses
                                key={category.label}
                                title={category.label}
                                validatorTypeId={category.validatorTypeId}
                                footer={footer}
                                getItemActions={(article: DossierArticle) =>
                                    getItemActions(article, category.validatorTypeId)
                                }
                                articles={processArticles}
                                statusLabel={getArticleProcessesStatusLabel(status)}
                                statusColor={getArticleProcessesStatusColor(status)}
                            />
                        );
                    })}
            </Box>
        </SkeletonLoader>
    );
};

export default DossierProcesses;
